import * as arrow from "apache-arrow";
import React, { useContext, useEffect, useRef, useState } from "react";

import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { isHistoricalDate, getFormattedCell } from "../../utils/mui_table_utils";
import { openSalesOrderCellPopover } from "../sales_order_cell_popover/store";

import { DatePivotParams } from "../../entities/date_pivot_params";
import { PivotViewConfig } from "../../entities/pivot_view_config";
import {
  getFlatTableData,
  getChunkedFlatTableData,
  getPivotTableData,
  getComparisonTableData,
} from "../../utils/mui_flat_table_data";
import { isChunkedParsedData } from "../../utils/parquet";
import {
  CHUNKED_MATRIX_TRANSFORM_IDS,
  useChunkedMatrixTableData,
} from "../../utils/chunked_inventory_health_matrix";
import type { FullParsedParquetData } from "../../utils/parquet";
import { MUIFlatTable } from "./MUIFlatTable";
import { useTheme } from "@mui/material/styles";
import { Box, IconButton } from "@mui/material";
import { AtomicLogo } from "../../assets/AtomicLogo";

export type TableType = "pivot" | "flat";

export interface UniversalTableViewerProps<
  T extends DatePivotParams = DatePivotParams,
> {
  parsedData:
    | { arrowTable: arrow.Table; columns: arrow.Field<any>[] }
    | import("../../utils/parquet").ChunkedParsedParquetData
    | null;
  viewParams: T;
  viewControllerConfig?: PivotViewConfig<T>;
  columnHeaderMap?: Record<string, string>;
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>;
  tableType: TableType | undefined;
  loading: boolean;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  columnSchema?: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  >;
  expandedGroups?: Set<string>;
  setExpandedGroups?: React.Dispatch<React.SetStateAction<Set<string>>>;
  fetchSortKeys?: (measure: string) => Promise<FullParsedParquetData | null>;
}

function ChunkedMatrixTableWrapper<T extends DatePivotParams = DatePivotParams>(
  props: {
    parsedData: import("../../utils/parquet").ChunkedParsedParquetData;
    viewParams: T;
    viewControllerConfig: PivotViewConfig<T>;
    sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
    setSortConfig: React.Dispatch<
      React.SetStateAction<
        Array<{ key: string; direction: "asc" | "desc"; index: number }>
      >
    >;
    expandedGroups?: Set<string>;
    setExpandedGroups?: React.Dispatch<React.SetStateAction<Set<string>>>;
    columnHeaderMap?: Record<string, string>;
    shadeHistory?: boolean;
    showHeaderRow?: boolean;
    fetchSortKeys?: (measure: string) => Promise<FullParsedParquetData | null>;
    sortDisplayedOnce: React.MutableRefObject<boolean>;
  }
) {
  const theme = useTheme();
  const {
    parsedData,
    viewParams,
    sortConfig,
    setSortConfig,
    expandedGroups = new Set(),
    setExpandedGroups = () => {},
    columnHeaderMap,
    shadeHistory,
    showHeaderRow,
    fetchSortKeys,
  } = props;
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const shouldExpandHeader = isHeaderExpanded || expandedGroups.has("*");

  const tableData = useChunkedMatrixTableData({
    chunkedData: parsedData,
    viewParams,
    expandedGroups,
    setExpandedGroups,
    theme,
    sortConfig,
    setSortConfig,
    fetchSortKeys,
    breakdownMeasures: props.viewControllerConfig?.breakdownMeasures,
    additionalMeasuresWithTotals: props.viewControllerConfig?.additionalMeasuresWithTotals,
    shadeHistory,
    plainDetailLabels: props.viewControllerConfig?.plainDetailLabels,
    enablePoCellPopover: props.viewControllerConfig?.enablePoCellPopover,
    enableTransferCellPopover:
      props.viewControllerConfig?.enableTransferCellPopover,
    showActualsForecastStrip:
      props.viewControllerConfig?.showActualsForecastStrip,
  });

  if (parsedData.rowCount === 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 3, color: "text.secondary" }}>
        No data found for the current filters.
      </Box>
    );
  }

  if (!tableData.isReady) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
        Loading table…
      </Box>
    );
  }

  if (!props.sortDisplayedOnce.current && !tableData.isSortReady) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
        Loading table…
      </Box>
    );
  }

  if (tableData.isSortReady) {
    props.sortDisplayedOnce.current = true;
  }

  return (
    <MUIFlatTable
      columns={tableData.columns}
      getRow={tableData.getRow}
      rowCount={tableData.rowCount}
      columnHeaderMap={columnHeaderMap}
      sortConfig={sortConfig}
      columnTotals={tableData.columnTotals}
      headerBand={tableData.headerBand}
      expandedGroups={expandedGroups}
      onToggleGroup={
        setExpandedGroups
          ? (groupKey: string) => {
              setExpandedGroups((prev) => {
                const next = new Set(prev);
                if (next.has(groupKey)) next.delete(groupKey);
                else next.add(groupKey);
                return next;
              });
            }
          : undefined
      }
      isHeaderExpanded={shouldExpandHeader}
      onToggleHeader={() => setIsHeaderExpanded(!isHeaderExpanded)}
      shadeHistory={shadeHistory}
      showHeaderRow={showHeaderRow}
    />
  );
}

export const UniversalTableViewer = <
  T extends DatePivotParams = DatePivotParams,
>(
  props: UniversalTableViewerProps<T>
) => {
  const {
    parsedData,
    viewParams,
    viewControllerConfig,
    columnHeaderMap,
    columnFormatters,
    tableType,
    loading,
    sortConfig,
    setSortConfig,
    columnSchema,
    expandedGroups,
    setExpandedGroups,
    fetchSortKeys,
  } = props;

  const theme = useTheme();
  const firstMount = useRef(true);
  const sortDisplayedOnce = useRef(false);

  // Header expansion state for comparison tables
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

  // ❯ ACTUALS / FORECAST strip on the generic (non-chunked-matrix) pivot
  // path — split at the active run's as-of date, like the matrix path.
  const { mainRun, lastSuccessfulRun } = useContext(VersionedDataContext);
  const genericRunAsOf = (mainRun ?? lastSuccessfulRun)?.createdAt ?? null;
  const [genericActualsCollapsed, setGenericActualsCollapsed] =
    useState(false);

  // Check if wildcard expansion is requested
  const shouldExpandHeader = isHeaderExpanded || expandedGroups?.has("*");

  useEffect(() => {
    if (parsedData && firstMount.current) {
      firstMount.current = false;
    }
  }, [parsedData]);

  // Sorting logic: clicking a column toggles its sort direction, single column sort by default, multi-column sort with shift key
  const handleSort = (
    key: string,
    e?: React.MouseEvent<HTMLTableCellElement>
  ) => {
    setSortConfig((prev) => {
      const isShiftPressed = e?.shiftKey || false;
      const existingIndex = prev.findIndex((s) => s.key === key);
      const existingSort = existingIndex >= 0 ? prev[existingIndex] : null;

      // If shift not pressed, start with empty array (single column sort)
      const baseConfig = isShiftPressed ? [...prev] : [];

      if (existingSort) {
        // Column is already sorted - toggle direction or remove
        const newDirection = existingSort.direction === "asc" ? "desc" : null;

        if (newDirection) {
          const index = isShiftPressed ? existingIndex : 0;
          baseConfig[index] = {
            key,
            direction: newDirection,
            index,
          };
          return baseConfig;
        }

        return baseConfig.filter((_, idx) => idx !== existingIndex);
      }

      // New column - add as ascending
      return [
        ...baseConfig,
        { key, direction: "asc", index: baseConfig.length },
      ];
    });
  };

  if (loading || !parsedData) {
    return null;
  }

  // Chunked matrix path: matrix-shaped parquet (supply_walk_parallelized_matrix, etc.)
  const effectiveTransformId =
    (viewControllerConfig as { chunkedMatrixTransformId?: string })
      ?.chunkedMatrixTransformId ?? viewControllerConfig?.transformId;
  if (
    isChunkedParsedData(parsedData) &&
    viewControllerConfig &&
    effectiveTransformId &&
    CHUNKED_MATRIX_TRANSFORM_IDS.has(effectiveTransformId)
  ) {
    return (
      <ChunkedMatrixTableWrapper
        parsedData={parsedData}
        viewParams={viewParams}
        viewControllerConfig={viewControllerConfig}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
        expandedGroups={expandedGroups}
        setExpandedGroups={setExpandedGroups}
        columnHeaderMap={columnHeaderMap}
        shadeHistory={viewControllerConfig.shadeHistory}
        showHeaderRow={viewControllerConfig.showHeaderRow}
        fetchSortKeys={fetchSortKeys}
        sortDisplayedOnce={sortDisplayedOnce}
      />
    );
  }

  // Use the new virtualization-friendly data functions
  let tableData;

  // Chunked path: flat table only, no full arrowTable
  if (isChunkedParsedData(parsedData)) {
    tableData = getChunkedFlatTableData({
      rowCount: parsedData.rowCount,
      getRow: parsedData.getRow,
      columns: parsedData.columns,
      sortConfig,
      onSort: handleSort,
      columnSchema,
      setSortConfig,
    });
  } else if (viewControllerConfig?.getMatrix) {
    // Use custom getMatrix function if provided, otherwise fall back to default logic
    tableData = viewControllerConfig.getMatrix({
      arrowTable: parsedData.arrowTable,
      columns: parsedData.columns,
      viewParams,
      demandWalkConfig: viewControllerConfig.demandWalkConfig,
      purchaseOrderConfig: viewControllerConfig.purchaseOrderConfig,
      purchasingCalendarConfig: viewControllerConfig.purchasingCalendarConfig,
      sortConfig,
      onSort: handleSort,
      setSortConfig,
      columnSchema: columnSchema || {},
      theme,
      expandedGroups: expandedGroups || new Set(),
      setExpandedGroups: setExpandedGroups || (() => {}),
      columnHeaderMap,
      firstMount,
    });
  } else {
    if (parsedData.columns.some((col) => col.name === "table_id")) {
      tableData = getComparisonTableData({
        arrowTable: parsedData.arrowTable,
        columns: parsedData.columns,
        viewParams,
        sortConfig,
        onSort: handleSort,
        columnSchema,
        setSortConfig,
        firstMount,
        expandedGroups: expandedGroups || new Set(),
        setExpandedGroups: setExpandedGroups || (() => {}),
      });
    } else if (tableType === "flat") {
      tableData = getFlatTableData({
        arrowTable: parsedData.arrowTable,
        columns: parsedData.columns,
        sortConfig,
        onSort: handleSort,
        columnSchema,
        setSortConfig,
        firstMount,
      });
    } else {
      tableData = getPivotTableData({
        arrowTable: parsedData.arrowTable,
        columns: parsedData.columns,
        viewParams,
        sortConfig,
        onSort: handleSort,
        columnSchema,
        setSortConfig,
        firstMount,
      });
    }
  }

  if (!tableData) {
    return null;
  }

  const { columns, getRow, rowCount, columnTotals } = tableData;

  // Generic-path ❯ ACTUALS / FORECAST strip: date columns carry epoch-ms
  // keys, so the split/tint/boundary can be applied generically over any
  // builder's columns without touching the builders themselves.
  let bandedColumns = columns;
  let genericHeaderBand:
    | import("./MUIFlatTable").MUIFlatTableHeaderBand
    | undefined;
  if (
    viewControllerConfig?.showActualsForecastStrip &&
    genericRunAsOf != null
  ) {
    const isDateKey = (k: string) => k !== "TOTAL" && !isNaN(Number(k));
    const dateKeys = columns.map((c: any) => c.key).filter(isDateKey);
    const actualsKeys = dateKeys.filter((k: string) =>
      isHistoricalDate(Number(k), genericRunAsOf)
    );
    if (actualsKeys.length > 0) {
      const actualsSet = new Set(actualsKeys);
      const lastActualsKey = actualsKeys[actualsKeys.length - 1];
      const firstDateIdx = columns.findIndex((c: any) => isDateKey(c.key));
      const lead = columns.slice(0, firstDateIdx);
      const trailing = columns
        .slice(firstDateIdx)
        .filter((c: any) => !isDateKey(c.key));
      const forecastCols = columns.filter(
        (c: any) => isDateKey(c.key) && !actualsSet.has(c.key)
      );
      if (genericActualsCollapsed) {
        const summaryCol = {
          key: "__actuals_summary",
          label: "ACTUALS Σ",
          width: 120,
          sortable: false,
          boundaryRight: true,
          bandTint: "#F4F6F8",
          renderCell: (row: any) => {
            let sum = 0;
            let any = false;
            actualsKeys.forEach((k: string) => {
              const n = Number(String(row[k] ?? "").replace(/,/g, ""));
              if (row[k] != null && row[k] !== "" && !isNaN(n)) {
                sum += n;
                any = true;
              }
            });
            return (
              <Box sx={{ px: 1, textAlign: "right", width: "100%" }}>
                {any ? sum.toLocaleString() : ""}
              </Box>
            );
          },
        };
        bandedColumns = [...lead, summaryCol, ...forecastCols, ...trailing];
      } else {
        bandedColumns = columns.map((c: any) => ({
          ...c,
          ...(actualsSet.has(c.key) ? { bandTint: "#F4F6F8" } : {}),
          ...(c.key === lastActualsKey ? { boundaryRight: true } : {}),
        }));
      }
      genericHeaderBand = {
        leadColumnCount: lead.length,
        actualsColumnCount: genericActualsCollapsed ? 1 : actualsKeys.length,
        forecastColumnCount: forecastCols.length,
        trailingColumnCount: trailing.length,
        collapsed: genericActualsCollapsed,
        onToggleCollapsed: () => setGenericActualsCollapsed((v) => !v),
        stickyLeftWidth: lead.reduce(
          (acc: number, c: any) =>
            acc + (c.sticky ? Number(c.width) || 0 : 0),
          0
        ),
      };
    }
  }

  // Sales-order popover (Demand): make ACTUALS date cells clickable — forecast
  // cells have no underlying orders. The cell opens the popover with the row's
  // active-segment values and the column's period; a table sibling hosts it
  // (see viewer/index.tsx), so opening never re-renders the table.
  let finalColumns = bandedColumns;
  if (viewControllerConfig?.enableSalesOrderPopover && genericRunAsOf != null) {
    const isDateKey = (k: string) => k !== "TOTAL" && !isNaN(Number(k));
    const segmentFields: string[] =
      (viewParams as { segments?: string[] })?.segments ?? [];
    // viewParams.dateHierarchy is the raw column (e.g. "order_placed_month"),
    // but the transform expects the short form ("month"). The view's
    // dateHierarchyColumnFilter maps column -> short; fall back to the raw
    // value for views that already store the short form (e.g. IH's "week").
    const rawHierarchy = (viewParams as { dateHierarchy?: string })
      ?.dateHierarchy;
    const hierarchyMap = (
      viewControllerConfig as {
        dateHierarchyColumnFilter?: Record<string, string>;
      }
    )?.dateHierarchyColumnFilter;
    const dateHierarchy = ((hierarchyMap?.[rawHierarchy ?? ""] ??
      rawHierarchy ??
      "week") as "day" | "week" | "month" | "quarter" | "year");
    finalColumns = bandedColumns.map((c: any) => {
      if (!isDateKey(c.key) || !isHistoricalDate(Number(c.key), genericRunAsOf!)) {
        return c;
      }
      const periodStartMs = Number(c.key);
      const baseRender = c.renderCell;
      return {
        ...c,
        renderCell: (row: any, rowIndex: number) => {
          const content = baseRender
            ? baseRender(row, rowIndex)
            : getFormattedCell({
                value: row[c.key],
                col: c.currencyKey ?? c.key,
                columnFormatters,
                row,
                theme,
                columnType: c.cellType,
                headerType: c.headerType,
                shadeHistory: viewControllerConfig?.shadeHistory,
                align: c.align,
              });
          const raw = row[c.key];
          const n = Number(String(raw ?? "").replace(/,/g, ""));
          const clickable = raw != null && raw !== "" && !isNaN(n) && n > 0;
          // All actuals date cells reserve a small left gutter so the value
          // never sits under the hover logo (and drillable / non-drillable
          // cells stay aligned within the column). The wrapper is
          // position:relative (block) so the shaded background still fills.
          return (
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                pl: "16px",
                boxSizing: "border-box",
                ...(clickable && {
                  "&:hover .salesCellLogoBtn": {
                    opacity: 1,
                    pointerEvents: "auto",
                  },
                }),
              }}
            >
              {content}
              {clickable && (
                <IconButton
                  className="salesCellLogoBtn"
                  aria-label="View sales orders"
                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                    e.stopPropagation();
                    const segments: Record<string, string> = {};
                    segmentFields.forEach((f) => {
                      if (row[f] != null) segments[f] = String(row[f]);
                    });
                    openSalesOrderCellPopover({
                      anchorEl:
                        (e.currentTarget.parentElement as HTMLElement) ??
                        e.currentTarget,
                      segments,
                      periodStartMs,
                      dateHierarchy,
                    });
                  }}
                  sx={{
                    position: "absolute",
                    left: -2,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 18,
                    height: 18,
                    padding: 0,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    "&:hover": { backgroundColor: "#fff" },
                    // Hidden until the cell is hovered (CSS-only reveal).
                    opacity: 0,
                    pointerEvents: "none",
                    transition: "opacity 80ms linear",
                  }}
                >
                  <AtomicLogo width={14} height={14} />
                </IconButton>
              )}
            </Box>
          );
        },
      };
    });
  }

  return (
    <MUIFlatTable
      columns={finalColumns}
      getRow={getRow}
      rowCount={rowCount}
      columnHeaderMap={columnHeaderMap}
      columnFormatters={columnFormatters}
      sortConfig={sortConfig}
      shadeHistory={viewControllerConfig?.shadeHistory}
      showHeaderRow={viewControllerConfig?.showHeaderRow}
      headerBand={genericHeaderBand}
      columnTotals={columnTotals}
      expandedGroups={expandedGroups}
      onToggleGroup={
        setExpandedGroups
          ? (groupKey: string) => {
              setExpandedGroups((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(groupKey)) {
                  newSet.delete(groupKey);
                } else {
                  newSet.add(groupKey);
                }
                return newSet;
              });
            }
          : undefined
      }
      isHeaderExpanded={shouldExpandHeader}
      onToggleHeader={() => setIsHeaderExpanded(!isHeaderExpanded)}
    />
  );
};

export { MUIFlatTable };
